
import React, { useState, useCallback, useMemo } from 'react';

export default function SubcategoryPanel({
  subcategories,
  onAddSub,
  onEditSub,
  onDeleteSub,
  isAdmin
}) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [copiedField, setCopiedField] = useState(null);

  const filteredSubcategories = useMemo(() => {
    if (!subcategories) return [];
    
    let filtered = [...subcategories];
    
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(sub =>
        sub.name?.toLowerCase().includes(q) ||
        sub.url?.toLowerCase().includes(q) ||
        sub.username?.toLowerCase().includes(q)
      );
    }
    
    return filtered;
  }, [subcategories, search]);

  const sortedSubcategories = useMemo(() => {
    return [...filteredSubcategories].sort((a, b) => {
      let va = a[sortKey] || '';
      let vb = b[sortKey] || '';
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (sortDir === 'asc') {
        return va > vb ? 1 : -1;
      } else {
        return va < vb ? 1 : -1;
      }
    });
  }, [filteredSubcategories, sortKey, sortDir]);

  const toggleSort = useCallback((key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }, [sortKey]);

  const togglePass = useCallback((id) => {
    setVisiblePasswords(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const copyToClipboard = useCallback(async (text, field, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(`${field}-${id}`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  const SortIcon = ({ col }) => (
    <span className="ml-1 text-surface-300 select-none">
      {sortKey === col ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
    </span>
  );

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h2 className="text-sm font-semibold text-surface-600 uppercase tracking-wide">
          Sites & Credentials
          <span className="ml-2 text-xs font-normal text-surface-400 normal-case">
            ({subcategories?.length || 0} sites)
          </span>
        </h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              className="pl-8 pr-3 py-1.5 text-xs bg-white border border-surface-200 rounded-lg placeholder-surface-400 focus:outline-none focus:ring-1 focus:ring-primary-500 w-44"
              placeholder="Search sites or credentials…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {isAdmin && (
            <button onClick={onAddSub} className="btn-primary text-xs py-1.5">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Site
            </button>
          )}
        </div>
      </div>

      {sortedSubcategories.length === 0 ? (
        <div className="card px-6 py-10 text-center text-surface-400 text-sm">
          {!subcategories || subcategories.length === 0 ? (
            isAdmin
              ? 'No sites yet. Click "+ Add Site" to create one.'
              : 'No sites available.'
          ) : (
            'No results match your search.'
          )}
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table w-full">
              <thead>
                <tr>
                  <th className="cursor-pointer select-none" onClick={() => toggleSort('name')}>
                    Site Name <SortIcon col="name" />
                  </th>
                  <th className="cursor-pointer select-none" onClick={() => toggleSort('url')}>
                    URL <SortIcon col="url" />
                  </th>
                  <th className="cursor-pointer select-none" onClick={() => toggleSort('username')}>
                    Username <SortIcon col="username" />
                  </th>
                  <th>Password</th>
                  {isAdmin && <th className="text-center">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sortedSubcategories.map(sub => (
                  <tr key={sub.id} className="group hover:bg-surface-50/60 transition-colors">
                    <td className="font-semibold text-surface-800">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                        </div>
                        <span>{sub.name}</span>
                      </div>
                    </td>
                    
                    <td>
                      {sub.url ? (
                        <a
                          href={sub.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-primary-600 hover:text-primary-700 font-medium hover:underline max-w-[200px] truncate text-sm"
                          onClick={e => e.stopPropagation()}
                        >
                          <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          <span className="truncate">{sub.url.length > 35 ? sub.url.substring(0, 35) + '…' : sub.url}</span>
                        </a>
                      ) : (
                        <span className="text-surface-400 text-xs">-</span>
                      )}
                    </td>
                    
                    <td>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs bg-surface-100 px-2 py-1 rounded">
                          {sub.username || '-'}
                        </span>
                        {sub.username && (
                          <button
                            onClick={e => { e.stopPropagation(); copyToClipboard(sub.username, 'username', sub.id); }}
                            className="text-surface-400 hover:text-primary-600 transition-colors"
                            title="Copy username"
                          >
                            {copiedField === `username-${sub.id}` ? (
                              <svg className="w-3.5 h-3.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                    
                    <td>
                      {sub.password ? (
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs bg-surface-100 px-2 py-1 rounded">
                            {visiblePasswords[sub.id] ? sub.password : '••••••••'}
                          </span>
                          <button
                            onClick={e => { e.stopPropagation(); togglePass(sub.id); }}
                            className="text-surface-400 hover:text-surface-600 transition-colors"
                            title={visiblePasswords[sub.id] ? 'Hide' : 'Show'}
                          >
                            {visiblePasswords[sub.id] ? (
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                              </svg>
                            ) : (
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={e => { e.stopPropagation(); copyToClipboard(sub.password, 'password', sub.id); }}
                            className="text-surface-400 hover:text-primary-600 transition-colors"
                            title="Copy password"
                          >
                            {copiedField === `password-${sub.id}` ? (
                              <svg className="w-3.5 h-3.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      ) : (
                        <span className="text-surface-400 text-xs">-</span>
                      )}
                    </td>
                    
                    {isAdmin && (
                      <td className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => onEditSub(sub)}
                            className="p-1.5 rounded-lg text-surface-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                            title="Edit site"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => onDeleteSub(sub)}
                            className="p-1.5 rounded-lg text-surface-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Delete site"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}